import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../core/services/auth.service';
import { UserStatus } from '../../shared/models';
import { Button } from '@openng/optimus-ui/button';
import { InputText } from '@openng/optimus-ui/inputtext';
import { PasswordModule } from '@openng/optimus-ui/password';

@Component({
  selector: 'app-claim',
  imports: [ReactiveFormsModule, Button, InputText, PasswordModule],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './claim.html'
})
export class ClaimComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  form!: FormGroup;
  token: string = '';
  isLoading = false;
  errorMessage = '';

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';

    // Validate token exists
    if (!this.token) {
      this.errorMessage = 'Invalid invitation link. Please request a new invitation from your administrator.';
      return;
    }

    this.form = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      position: ['']
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get('password')?.value === g.get('confirmPassword')?.value
      ? null : { mismatch: true };
  }

  submit(): void {
    if (this.form.invalid || !this.token) return;
    this.isLoading = true;
    this.errorMessage = '';

    const body = {
      token: this.token,
      password: this.form.get('password')?.value,
      confirmPassword: this.form.get('confirmPassword')?.value,
      fullName: this.form.get('fullName')?.value,
      position: this.form.get('position')?.value || ''
    };

    this.api.post<any>('/Invites/claim', body, { skipToast: true }).subscribe({
      next: (res) => {
        const accessToken = res?.accessToken;
        const user = res?.user;
        const expiresAt = res?.expiresAt;
        const mustChangePassword = !!res?.mustChangePassword;

        if (accessToken && user) {
          // establish authenticated session consistently with Login flow
          this.authService.establishSession(user, accessToken, expiresAt);

          // route based on user state and flags
          if (user.status === UserStatus.Inactive) {
            this.router.navigate(['/frozen-account']);
          } else if (mustChangePassword) {
            this.router.navigate(['/change-password']);
          } else {
            const role = user.systemRole ?? 0;
            const target = (role === 3 || role === 4) ? '/plugins' : '/team-management';
            this.router.navigate([target]);
          }
        } else {
          // Fallback: no token or user returned
          this.router.navigate(['/login']);
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;

        // Handle different error scenarios
        if (err.status === 400) {
          if (err.message?.includes('already') || err.message?.includes('activated') || err.message?.includes('registered')) {
            this.errorMessage = 'This invitation has already been used. Please contact your administrator if you need access to your account.';
          } else if (err.message?.includes('expired') || err.message?.includes('invalid')) {
            this.errorMessage = 'This invitation link has expired or is invalid. Please request a new invitation from your administrator.';
          } else {
            this.errorMessage = err.message || 'Failed to accept invite';
          }
        } else if (err.status === 403) {
          if (err.message?.includes('frozen')) {
            this.router.navigate(['/frozen-account']);
          } else {
            this.errorMessage = 'Access denied. Please contact your administrator.';
          }
        } else if (err.status === 500) {
          // Check for specific 500 errors that indicate already claimed invite
          if (err.message?.includes('Invalid or expired invite token') ||
              err.error?.includes('Invalid or expired invite token') ||
              err.message?.includes('UnauthorizedAccessException') ||
              err.error?.includes('UnauthorizedAccessException')) {
            this.errorMessage = 'This invitation has already been used or is no longer valid. Please contact your administrator if you need access to your account.';
          } else {
            this.errorMessage = 'An error occurred while processing your invitation. Please try again or contact your administrator.';
          }
        } else {
          this.errorMessage = err.message || 'Failed to accept invite';
        }
      }
    });
  }

  get passwordError(): string {
    const control = this.form.get('password');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'Password is required';
      if (control.errors['minlength']) return 'Password must be at least 8 characters';
    }
    return '';
  }

  get confirmPasswordError(): string {
    const control = this.form.get('confirmPassword');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'Please confirm your password';
    }
    if (this.form.errors?.['mismatch']) {
      return 'Passwords do not match';
    }
    return '';
  }

  get fullNameError(): string {
    const control = this.form.get('fullName');
    if (control?.errors && control.touched) {
      if (control.errors['required']) return 'Full name is required';
      if (control.errors['minlength']) return 'Full name must be at least 2 characters';
    }
    return '';
  }

  get positionError(): string {
    const control = this.form.get('position');
    return '';
  }
}
